import { useState, useEffect, createContext, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User, AuthState, RegisterRequest } from '../types';
import { authService } from '../services';

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: RegisterRequest) => Promise<void>;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null, // Token handled by cookies, not exposed to JS
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const initAuth = async () => {
      // Na OAuth callback strankach nedelame initAuth - token se teprve ziskava
      const isAuthCallback =
        window.location.pathname.includes('/auth/') &&
        window.location.pathname.includes('/callback');
      if (isAuthCallback) {
        setAuthState((prev) => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        const user = await authService.getCurrentUser();
        setAuthState({
          user,
          token: null,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
    try {
      await authService.login({ email, password });

      // Verify session immediately to ensure cookie is set and working
      const user = await authService.getCurrentUser();

      setAuthState({
        user,
        token: null,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Login failed',
      }));
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      queryClient.clear();
      setAuthState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    }
  };

  const register = async (userData: RegisterRequest) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    await authService.register(userData);

    setAuthState((prev) => ({ ...prev, isLoading: false }));
  };

  const updateProfile = async (userData: Partial<User>) => {
    const updatedUser = await authService.updateProfile(userData);
    setAuthState((prev) => ({ ...prev, user: updatedUser }));
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await authService.changePassword(currentPassword, newPassword);
  };

  const refreshUser = async () => {
    try {
      const user = await authService.getCurrentUser();
      setAuthState((prev) => ({ ...prev, user, isAuthenticated: true }));
    } catch (error) {
      console.error('Failed to refresh user:', error);
      // Optional: logout if refresh fails?
    }
  };

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
    refreshUser,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Export AuthContext for the separate useAuth hook
export { AuthContext };
